<template>
  <div
    class="top-bar flex flex-shrink-0 bg-surface-0 dark:bg-surface-900 relative w-full justify-between items-center z-[1500] px-4 py-8 md:py-5 lg:py-6 drop-shadow-lg"
    v-bind="$attrs"
  >
    <!-- Left-side content -->
    <div class="flex items-center gap-4">
      <!-- Media Query Breakpoint Indicator (visible only in debug mode) -->
      <div
        v-if="isDebug"
        class="breakpoint-indicator px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded border border-blue-300 dark:border-blue-700"
      >
        {{ currentBreakpoint }}
      </div>

      <!-- Pulsante per tornare alla Home -->
      <button
        v-if="shouldShowGoToMapButton"
        class="back-btn flex text-surface-900 dark:text-surface-0 items-center"
        @click="goToMap"
        :title="t('principal.topbar.tooltip.map')"
      >
        <i class="pi pi-angle-left mr-2"></i>
        <!-- Big screen title -->
        <span class="hidden md:block">{{
          t("principal.topbar.button.map")
        }}</span>
        <!-- Small screen title -->
        <span class="md:hidden">{{
          t("principal.topbar.button.map_small")
        }}</span>
      </button>
    </div>

    <!-- Logo Centrale -->
    <div
      class="logo-container flex justify-center items-center fixed left-1/2 transform -translate-x-1/2"
    >
      <img
        :src="logoSrc"
        alt="Agricobots Logo"
        class="logo cursor-pointer select-none"
        @click="goToMap"
      />
    </div>

    <!-- Pulsanti Login/Logout, Notifications, Drawer Menu -->
    <div
      class="auth-buttons flex items-center fixed right-4 gap-4"
      :class="notifications.unreadCount > 0 ? 'mt-[3px] md:mt-0' : ''"
    >
      <!-- Info Icon Button (visible on all screens) -->
      <button
        v-if="authStore.isLoggedIn"
        class="info-btn flex items-center justify-center w-6 h-6 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
        @click="toggleAppInfoDialog"
        :title="t('principal.topbar.tooltip.app_info')"
        type="button"
      >
        <i
          class="pi pi-info-circle text-surface-600 dark:text-surface-400 text-sm"
        ></i>
      </button>

      <!-- User Info (hidden on mobile, visible on desktop) -->
      <div
        v-if="authStore.isLoggedIn"
        class="hidden lg:flex user-info items-center gap-2 mr-2"
      >
        <!-- Time Sync Info -->
        <span
          v-if="isDebug && timeSyncInfo"
          class="text-surface-600 dark:text-surface-400 text-sm"
        >
          {{ timeSyncInfo }}
        </span>
        <!-- Debug Mode Indicator (only visible in debug mode) -->
        <div
          v-if="isDebug"
          class="debug-indicator px-2 py-1 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 text-xs rounded border border-orange-300 dark:border-orange-700 cursor-help"
          :title="debugReason"
        >
          DEBUG
        </div>

        <!--
          Simulator menu. Rendered only in a dev build: SimDevMenu resolves to
          null anywhere else, so this never draws in a deployed app - not even on
          the localhost/private-network hosts where the DEBUG badge above does.
        -->
        <component :is="SimDevMenu" v-if="SimDevMenu" />

        <!-- Email and role -->
        <div class="flex items-center gap-1">
          <span class="text-surface-900 dark:text-surface-0">{{
            authStore.getUser()?.email
          }}</span>
          <span class="text-surface-600 dark:text-surface-400"
            >({{ userDisplayLabel }})</span
          >
        </div>
      </div>

      <!-- Notifications bell -->
      <div @click.stop="toggleDropdown" class="relative -mb-0.5 cursor-pointer">
        <BadgedIcon
          icon="pi pi-bell"
          :class="bellIsGray ? '' : 'text-primary'"
          :value="notifications.unreadCount"
        />
        <NotificationsDropdown
          :visible="dropdownOpen"
          :items="notifications.items"
          :unreadCount="notifications.unreadCount"
          @close="dropdownOpen = false"
          @load-more="onLoadMore"
          @mark-read="onMarkRead"
          @mark-unread="onMarkUnread"
          @remove="onRemove"
        />
      </div>

      <!-- Hamburger Menu Button (opens drawer) -->
      <button
        v-if="authStore.isLoggedIn"
        class="menu-btn flex items-center"
        @click="toggleDrawer"
        type="button"
        :title="t('principal.topbar.tooltip.menu')"
      >
        <i class="pi pi-bars text-surface-900 dark:text-surface-0"></i>
      </button>
    </div>
  </div>

  <!-- Application Info Dialog -->
  <AppInfoDialog
    :visible="showAppInfoDialog"
    @update:visible="showAppInfoDialog = $event"
    :app-info="appInfo"
  />

  <!-- Drawer Menu -->
  <AppDrawer
    :visible="drawerVisible"
    @update:visible="drawerVisible = $event"
  />
</template>

<script setup>
import agricobotsLogo from "@/assets/logo.png";
import AppDrawer from "@/components/AppDrawer.vue";
import BadgedIcon from "@/components/badged-icon.vue";
import NotificationsDropdown from "@/components/notifications/NotificationsDropdown.vue";
import { useDebugMode } from "@/composables/useDebugMode";
import { useLayout } from "@/layout/composables/layout";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications-store";
import { TimeSync } from "@/utils/timesync/timesync";
import AppInfoDialog from "@/views/dialogs/AppInfoDialog.vue";
import {
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

/**
 * SIM badge, next to DEBUG. Dev builds only.
 *
 * Vite replaces `import.meta.env.DEV` with a literal, so a production build
 * evaluates this to `null` and Rollup drops the dynamic import together with the
 * whole simulator. It must stay a conditional dynamic import - a static one
 * would pull the simulator into every bundle.
 *
 * Note this is NOT tied to useDebugMode().isDebug: that is also true for a
 * production build opened on localhost or a private network.
 */
const SimDevMenu = import.meta.env.DEV
  ? defineAsyncComponent(() => import("@/dev/SimMenu.vue"))
  : null;

const router = useRouter();
const { t } = useI18n({
  useScope: "global",
});
const authStore = useAuthStore();
const notifications = useNotificationsStore();

// Debug mode composable
const { isDebug, debugReason, logDebug } = useDebugMode();

const { isDarkTheme, toggleDarkMode } = useLayout();
/**
 * Bundled from src/assets rather than referenced out of public/: Vite hashes it,
 * so a new logo busts the cache instead of lingering in browsers, and the brand
 * mark lives in exactly one place for the whole app.
 */
const logoSrc = agricobotsLogo;

const userRole = ref("");

// Initialize user role
onMounted(async () => {
  const role = await authStore.getUserRole();
  userRole.value = t(`common.customer_roles.${role}`);
});

// Computed property to show either role or "No Company"
const userDisplayLabel = computed(() => {
  const hasCompany = authStore.hasEffectiveCompany;
  if (!hasCompany) {
    return t("common.no_company_label");
  }
  // Show admin label if user is admin
  if (authStore.isAdmin) {
    return t("common.admin_user_label");
  }
  return userRole.value;
});

const goToMap = () => {
  router.push({ name: "map" });
};

const login = () => {
  router.push({ name: "login" });
};

const logout = async () => {
  await authStore.logout();
  router.push({ name: "login" });
};

// Drawer state management
const drawerVisible = ref(false);

/**
 * Toggles the drawer visibility.
 */
const toggleDrawer = () => {
  console.log(
    "[SecondaryTopBar] Toggling drawer, currently:",
    drawerVisible.value,
  );
  drawerVisible.value = !drawerVisible.value;
};

// Add these new computed properties and initialization (after existing computed properties, around line 162)
/**
 * Computed property for displaying synced time, offset, and last sync info.
 * @returns {string | null} Formatted string with time details, or null if sync failed.
 */
const timeSyncInfo = computed(() => {
  const currentTime = TimeSync.now();
  const offset = TimeSync.getOffset();
  const lastSync = TimeSync.getLastSync();
  if (!lastSync) return null; // No sync yet—hide the label
  return `${currentTime.toLocaleString()} (offset: ${offset}ms, last sync: ${lastSync.toLocaleString()})`;
});

// Reactive window width tracking
const windowWidth = ref(0);

// Update window width on mount and resize
onMounted(() => {
  if (typeof window !== "undefined") {
    windowWidth.value = window.innerWidth;
    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
    };
    window.addEventListener("resize", updateWidth);
    // Cleanup listener on unmount
    onUnmounted(() => {
      window.removeEventListener("resize", updateWidth);
    });
  }
});

/**
 * Computed property for displaying the current Tailwind CSS breakpoint (media query).
 * @returns {string} Current breakpoint name (xs, sm, md, lg, xl, 2xl).
 */
const currentBreakpoint = computed(() => {
  const width = windowWidth.value;
  if (width < 640) return "xs";
  if (width < 768) return "sm";
  if (width < 1024) return "md";
  if (width < 1280) return "lg";
  if (width < 1536) return "xl";
  return "2xl";
});

const shouldShowGoToMapButton = computed(() => {
  return router.currentRoute.value.meta.allowSecondaryTopBarGoToMap === true;
});

// Notifications dropdown logic
const dropdownOpen = ref(false);

// Application info data
const appInfo = reactive({
  version: import.meta.env.VITE_APP_VERSION || "1.0.0",
  buildDate: import.meta.env.VITE_BUILD_DATE || new Date().toLocaleDateString(),
  environment:
    import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "development",
  features: import.meta.env.VITE_APP_FEATURES
    ? import.meta.env.VITE_APP_FEATURES.split(",").map((feature) =>
        feature.trim(),
      )
    : [
        "Digital Asset Management",
        "ROS Integration",
        "Real-time Notifications",
        "Multi-tenant Architecture",
      ],
});
function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value;
}
async function onLoadMore() {
  const page = Math.floor(notifications.items.length / 20) + 1;
  await notifications.getAll({ page, limit: 20 });
}
async function onMarkRead(id) {
  await notifications.markRead(id);
}
async function onMarkUnread(id) {
  await notifications.markUnread(id);
}
async function onRemove(id) {
  await notifications.remove(id);
}
const bellIsGray = computed(() => notifications.unreadCount === 0);

// App Info Dialog state and logic
const showAppInfoDialog = ref(false);

/**
 * Toggles the application info dialog visibility.
 */
const toggleAppInfoDialog = () => {
  showAppInfoDialog.value = !showAppInfoDialog.value;
};
</script>

<style scoped>
.robot {
  width: 1.25em;
  height: 1.25em;
  --svg: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' d='M9 15a1 1 0 1 0 1 1a1 1 0 0 0-1-1m-7-1a1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-1-1m20 0a1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-1-1m-5-7h-4V5.72A2 2 0 0 0 14 4a2 2 0 0 0-4 0a2 2 0 0 0 1 1.72V7H7a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3m-3.28 2l-.5 2h-2.44l-.5-2ZM18 19a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h1.22L9 12.24a1 1 0 0 0 1 .76h4a1 1 0 0 0 1-.76L15.78 9H17a1 1 0 0 1 1 1Zm-3-4a1 1 0 1 0 1 1a1 1 0 0 0-1-1'/%3E%3C/svg%3E");
  background-color: currentColor;
  -webkit-mask-image: var(--svg);
  mask-image: var(--svg);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
}

.home-btn,
.login-btn,
.logout-btn,
.menu-btn {
  background-color: transparent;
  border: none;
  color: #333;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.home-btn:hover,
.login-btn:hover,
.logout-btn:hover,
.menu-btn:hover {
  color: #007bff;
}

.logo-container img {
  max-height: 30px; /* Imposta l'altezza massima del logo */
}
</style>
